import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import { PageTitleContainer } from '../components/common';
import WebsiteRequirementsWizard from '../components/projects/WebsiteRequirementsWizard';
import useProject from '../hooks/useProject';
import useNotification from '../hooks/useNotification';

const WebsiteRequirements = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getProject, loading, error } = useProject();
  const { showNotification } = useNotification();
  const [project, setProject] = useState(null);
  const [loadingProject, setLoadingProject] = useState(true);

  // State for content height
  const [contentHeight, setContentHeight] = useState('calc(100vh - 130px)');

  // Calculate content height based on window size and header heights
  useLayoutEffect(() => {
    const updateContentHeight = () => {
      // Default header heights (fallback values if refs aren't available)
      const mainHeaderHeight = 73;  // Main app header
      const titleHeaderHeight = 80; // Create project header
      const extraPadding = 84;      // Additional padding for spacing

      const totalHeaderHeight = mainHeaderHeight + titleHeaderHeight + extraPadding;
      setContentHeight(`calc(100vh - ${totalHeaderHeight}px)`);
    };

    // Initial calculation
    updateContentHeight();

    // Re-calculate on window resize
    window.addEventListener('resize', updateContentHeight);

    return () => {
      window.removeEventListener('resize', updateContentHeight);
    };
  }, []);

  // Fetch project details
  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoadingProject(true);
        const projectData = await getProject(projectId);
        if (projectData) {
          setProject(projectData);

          // Check if project type is website
          if (projectData.applicationType !== 'website') {
            showNotification({
              message: 'This wizard is only for website projects',
              type: 'warning'
            });
            navigate(`/projects/${projectId}`);
          }

          // Check if requirements already completed
          if (projectData.websiteRequirements?.completed) {
            navigate(`/projects/${projectId}/prd-workflow`);
          }
        }
      } catch (err) {
        console.error('Error fetching project:', err);
        showNotification({
          message: 'Failed to load project details',
          type: 'error'
        });
      } finally {
        setLoadingProject(false);
      }
    };

    if (projectId) {
      fetchProject();
    }
  }, [projectId, getProject, navigate, showNotification]);

  const handleComplete = () => {
    // Navigate to PRD workflow page to start working on activated tiles
    navigate(`/projects/${projectId}/prd-workflow`);
  };

  const handleCancel = () => {
    // Navigate back to projects list
    navigate('/projects');
  };

  if (loadingProject || loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: '#f5f6f7'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100%',
      bgcolor: '#f5f6f7',
      minHeight: '100vh',
      pb: 4,
      px: 6,
      pt: 4,
      overflowX: 'hidden'
    }}>
      <PageTitleContainer
        title="Website Requirements"
        subtitle={project?.name || 'Configure your website preferences'}
        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          ml: 0,
          mr: 0,
          mb: 2,
          mt: 0,
          py: '16px'
        }}
        titleProps={{
          sx: { fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }
        }}
      />

      <Paper
        elevation={0}
        sx={{
          borderRadius: '4px',
          p: { xs: 2, sm: "12px 12px 12px 12px" },
          width: '100%',
          height: contentHeight,
          bgcolor: 'white',
          overflowX: 'hidden',
          overflowY: 'auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        }}
      >
        <WebsiteRequirementsWizard
          projectId={projectId}
          onComplete={handleComplete}
          onCancel={handleCancel}
        />
      </Paper>
    </Box>
  );
};

export default WebsiteRequirements;